import { ref, type Component, computed, markRaw } from 'vue';
import { defineStore } from 'pinia';
import type { ButtonV2Props } from '../ButtonV2/button-v2';

/**
 * Each Modal Dialog returns a Promise with some specified value (ModalResult).
 * The Component rendered in the Modal automatically gets the resolve
 * function via props.
 * Use the resolve function to close the dialog and return the specified value.
 * Closing the dialog via x button or clicking outside will resolve with a
 * cancelled result.
 * The modals are organised as a stack (array). Each modal gets a unique id.
 * Title and size can be specified for each modal, the rest is handled within
 * the component that is rendered in the modal.
 *
 * HOW TO USE:
 *
 * 1. Create your Component that should be rendered inside a modal.
 *    - export the type for the Props.
 *    - The type for the props should intersect with ModalV2Props
 *      example: & ModalV2Props<string> (string is your desired result type)
 *    - If you forget this, you won't get the resolve function in props.
 *    - You may use the <ModalV2DefaultFooter> component to render buttons.
 *
 * 2. Use your component by calling the stores openModal function.
 *    - use the first generic type argument to specify the props type.
 *    - use the second generic type argument to specify the result type.
 *    - If you forget this, props aren't passed typesafe and result is unknown.
 *    - example:
 *
 *           modalStore.openModal<TestModalProps, string>({
 *             title: 'test',
 *             size: 'md',
 *             component: TestModal,
 *             props: {
 *               text: "Some text that TestModal expects"
 *             },
 *           })
 *
 * 3. Make sure you render the <ModalV2Renderer> component somewhere in your app
 *    - it renders all modals in the stack.
 */

export type ModalResult<T> = {
  value: T;
  cancelled: boolean;
};

// resolve/reject is provided below
export type ModalV2Props<T> = {
  resolve: (result: ModalResult<T>) => void;
  reject: () => void;
  id: string;
};

export type ModalV2FooterButtonProps = ButtonV2Props & {
  onClick?: () => void;
};

export type ModalV2<ModalProps> = {
  title?: string; // not applied for entire-screen
  /**
   * A Vue component to render inside the modal. Render with:
   * <component :is="modal.component" v-bind="modal.props" />
   */
  component: Component;
  props: ModalProps;
  size: 'sm' | 'md' | 'lg' | 'xl' | 'full' | 'entire-screen';
  // props to Omit because they are not passed from the consumer
  isOpen: boolean;
  id: string;
};

export const useModalStore = defineStore('modal', () => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const _modals = ref<ModalV2<ModalV2Props<any>>[]>([]);

  const modals = computed(() => _modals);

  const closeModal = (id?: string) => {
    const modalToClose = id
      ? _modals.value.find((modal) => modal.id === id)
      : _modals.value[_modals.value.length - 1];

    if (!modalToClose) {
      return;
    }

    modalToClose.isOpen = false;
    setTimeout(() => {
      const modalIndex = _modals.value.findIndex(
        (modal) => modal.id === modalToClose.id,
      );
      if (modalIndex === -1) {
        return;
      }
      _modals.value.splice(modalIndex, 1);
      // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    }, 200);
  };

  const closeAllModals = () => {
    _modals.value = [];
  };

  const openModal = <ModalProps, T>(
    modal: Omit<
      ModalV2<Omit<ModalProps, 'resolve' | 'reject' | 'isOpen' | 'id'>>,
      'id' | 'isOpen'
    >,
  ): Promise<ModalResult<T>> => {
    const id = crypto.randomUUID();

    return new Promise((resolve, reject) => {
      const modalProps = markRaw({
        ...modal.props,
        id,
        resolve: (result: ModalResult<T>) => {
          resolve(result);
          closeModal(id);
        },
        reject: (reason?: string) => {
          reject(reason);
          closeModal(id);
        },
      });

      const modalEntry: ModalV2<ModalV2Props<T>> = {
        ...modal,
        id,
        isOpen: false,
        component: markRaw(modal.component),
        props: modalProps,
      };

      _modals.value.push(modalEntry);

      // Trigger enter transition after initial mount.
      setTimeout(() => {
        const currentModal = _modals.value.find((m) => m.id === id);
        if (!currentModal) {
          return;
        }
        currentModal.isOpen = true;
      }, 0);
    });
  };

  return { openModal, modals, closeModal, closeAllModals };
});
